/**
 * Yup validation schema for case template form.
 *
 * This schema provides validation for the entire CaseTemplateFormValues object,
 * ensuring all fields are validated regardless of which step is currently mounted.
 */
import * as yup from "yup";

import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { MAX_DESCRIPTION_LENGTH } from "src/cases/caseView/constants";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { isLikePythonKey } from "src/utils/pythonUtils";

// =============================================================================
// CONSTANTS
// =============================================================================

const TASK_NAME_MAX_LENGTH = 50;
const TASK_KEY_MAX_LENGTH = 50;
const CASE_ACTION_NAME_MAX_LENGTH = 50;
const CASE_ACTION_KEY_MAX_LENGTH = 50;
const FORM_FIELD_NAME_MAX_LENGTH = 50;
const FORM_FIELD_KEY_MAX_LENGTH = 50;

// Reserved keys that cannot be used for user-defined properties
const RESERVED_SYSTEM_KEYS = [
  "queue_id",
  "assignee_id",
  "updated_at",
  "created_at",
  "updated_by",
  "env",
  "id",
  "comment",
] as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a key is valid (lowercase letters, numbers, underscores, starts with a letter)
 */
const isValidKey = (key: string): boolean => {
  const validKeyPattern = /^[a-z][a-z0-9_]*$/;
  return validKeyPattern.test(key);
};

/**
 * Check if a key is a reserved system key
 */
const isReservedSystemKey = (key: string): boolean => {
  return RESERVED_SYSTEM_KEYS.includes(
    key as (typeof RESERVED_SYSTEM_KEYS)[number],
  );
};

// =============================================================================
// PREDEFINED VALUE SCHEMAS
// =============================================================================

const predefinedSubValueSchema = yup.object({
  value: yup
    .string()
    .required("Value is required")
    .max(
      DEFAULT_STRING_LENGTH,
      `Value must be less than ${DEFAULT_STRING_LENGTH} characters`,
    )
    .test(
      "pythonVariable",
      "Sub-status value must begin with a letter, and may only include letters, numbers, and underscores",
      (value) => !value || isLikePythonKey(value),
    ),
  color: yup.string().nullable(),
  display_name: yup
    .string()
    .max(
      DEFAULT_STRING_LENGTH,
      `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
    )
    .nullable(),
  _isNewlyAppended: yup.boolean().optional(),
  _internalId: yup.string().optional(),
});

const predefinedValueSchema = yup.object({
  value: yup
    .string()
    .required("Value is required")
    .max(
      DEFAULT_STRING_LENGTH,
      `Value must be less than ${DEFAULT_STRING_LENGTH} characters`,
    ),
  color: yup.string().nullable(),
  display_name: yup
    .string()
    .max(
      DEFAULT_STRING_LENGTH,
      `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
    )
    .nullable(),
  sub_values: yup.array().of(predefinedSubValueSchema).nullable(),
  _isNewlyAppended: yup.boolean().optional(),
  _internalId: yup.string().optional(),
});

// =============================================================================
// CASE PROPERTY SCHEMA
// =============================================================================

const casePropertySchema = yup
  .object({
    key: yup
      .string()
      .required("Property name is required")
      .max(
        DEFAULT_STRING_LENGTH,
        `Property name must be less than ${DEFAULT_STRING_LENGTH} characters`,
      )
      .test(
        "pythonVariable",
        "Property name must begin with a letter, and may only include letters, numbers, and underscores",
        (value) => !value || isLikePythonKey(value),
      )
      .test(
        "reservedSystemKey",
        ({ value }) =>
          `"${value}" is a reserved system field and cannot be used`,
        (value) => !value || !isReservedSystemKey(value),
      ),
    display_name: yup
      .string()
      .required("Display name is required")
      .max(
        DEFAULT_STRING_LENGTH,
        `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
      ),
    type: yup.string().required(),
    values: yup.array().of(predefinedValueSchema).nullable(),
    entity_type: yup.string().nullable(),
    event_type: yup.string().nullable(),
    _isNewlyAppended: yup.boolean().optional(),
  })
  .test(
    "statusValuesPythonVariable",
    "Status value must begin with a letter, and may only include letters, numbers, and underscores",
    function (property) {
      if (!property || property.key !== "status" || !property.values) {
        return true;
      }
      for (let i = 0; i < property.values.length; i++) {
        const val = property.values[i]?.value;
        if (val && !isLikePythonKey(val)) {
          return this.createError({
            path: `${this.path}.values[${i}].value`,
            message:
              "Status value must begin with a letter, and may only include letters, numbers, and underscores",
          });
        }
      }
      return true;
    },
  );

const casePropertiesArraySchema = yup
  .array()
  .of(casePropertySchema)
  .test(
    "uniquePropertyKeys",
    "Property keys must be unique",
    function (properties) {
      if (!properties) return true;
      const keys = properties.map((prop) => prop.key).filter(Boolean);
      const uniqueKeys = new Set(keys);
      if (keys.length !== uniqueKeys.size) {
        const duplicates = keys.filter(
          (key, index) => keys.indexOf(key) !== index,
        );
        if (duplicates.length > 0) {
          // Find the index of the first duplicate
          const firstDuplicateIndex = properties.findIndex(
            (prop, index) =>
              prop.key === duplicates[0] &&
              properties.findIndex((p) => p.key === duplicates[0]) !== index,
          );
          return this.createError({
            path: `${this.path}[${firstDuplicateIndex}].key`,
            message: `Property name "${duplicates[0]}" is already used by another case property`,
          });
        }
      }
      return true;
    },
  );

// =============================================================================
// FILE CATEGORY SCHEMA
// =============================================================================

const fileCategorySchema = yup.object({
  key: yup
    .string()
    .required("Category key is required")
    .max(
      DEFAULT_STRING_LENGTH,
      `Category key must be less than ${DEFAULT_STRING_LENGTH} characters`,
    )
    .test(
      "fileCategoryKeyFormat",
      "Category key must contain only lowercase letters, numbers, and underscores.",
      (value) => !value || isValidKey(value),
    ),
  display_name: yup
    .string()
    .required("Category name is required")
    .max(
      DEFAULT_STRING_LENGTH,
      `Category name must be less than ${DEFAULT_STRING_LENGTH} characters`,
    ),
  description: yup
    .string()
    .max(
      DEFAULT_STRING_LENGTH,
      `Description must be less than ${DEFAULT_STRING_LENGTH} characters`,
    )
    .nullable(),
  _isDefault: yup.boolean().optional(),
  _isNewlyAppended: yup.boolean().optional(),
});

const fileCategoriesArraySchema = yup
  .array()
  .of(fileCategorySchema)
  .test(
    "uniqueFileCategoryKeys",
    "Category keys must be unique",
    function (categories) {
      if (!categories) return true;
      const keys = categories
        .filter((cat) => !cat._isDefault)
        .map((cat) => cat.key)
        .filter(Boolean);
      const uniqueKeys = new Set(keys);
      if (keys.length !== uniqueKeys.size) {
        const duplicates = keys.filter(
          (key, index) => keys.indexOf(key) !== index,
        );
        if (duplicates.length > 0) {
          const firstDuplicateIndex = categories.findIndex(
            (cat, index) =>
              !cat._isDefault &&
              cat.key === duplicates[0] &&
              categories.findIndex(
                (c) => !c._isDefault && c.key === duplicates[0],
              ) !== index,
          );
          return this.createError({
            path: `${this.path}[${firstDuplicateIndex}].key`,
            message: `Category key "${duplicates[0]}" is already used by another file category`,
          });
        }
      }
      return true;
    },
  );

// =============================================================================
// FORM FIELD SCHEMA (for tasks and actions)
// =============================================================================

const formFieldSchema = yup.object({
  key: yup
    .string()
    .required("Field key cannot be empty")
    .max(
      FORM_FIELD_KEY_MAX_LENGTH,
      `Field key must not be more than ${FORM_FIELD_KEY_MAX_LENGTH} characters`,
    )
    .test(
      "formFieldKeyFormat",
      "Field key must contain only lowercase letters, numbers, and underscores.",
      (value) => !value || isValidKey(value),
    ),
  display_name: yup
    .string()
    .required("Field name cannot be empty")
    .max(
      FORM_FIELD_NAME_MAX_LENGTH,
      `Field name must not be more than ${FORM_FIELD_NAME_MAX_LENGTH} characters`,
    ),
  type: yup.string().required(),
  required: yup.boolean().optional(),
  description: yup
    .string()
    .max(
      MAX_DESCRIPTION_LENGTH,
      `Field description must not be more than ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters`,
    )
    .nullable(),
  display_as_textarea: yup.boolean().optional(),
  options: yup
    .array()
    .of(
      yup
        .string()
        .required("Option cannot be empty")
        .max(100, "Option must not be more than 100 characters"),
    )
    .test("uniqueOptions", "Options must be unique", function (options) {
      if (!options) return true;
      const nonEmptyOptions = options.filter(Boolean);
      const uniqueOptions = new Set(nonEmptyOptions);
      if (nonEmptyOptions.length !== uniqueOptions.size) {
        const duplicates = nonEmptyOptions.filter(
          (option, index) => nonEmptyOptions.indexOf(option) !== index,
        );
        if (duplicates.length > 0) {
          const firstDuplicateIndex = options.findIndex(
            (option, index) =>
              option === duplicates[0] &&
              options.findIndex((o) => o === duplicates[0]) !== index,
          );
          return this.createError({
            path: `${this.path}[${firstDuplicateIndex}]`,
            message: `Option "${duplicates[0]}" is already used`,
          });
        }
      }
      return true;
    })
    .nullable(),
});

const formSchema = yup
  .object({
    fields: yup
      .array()
      .of(formFieldSchema)
      .test("uniqueFieldKeys", "Field keys must be unique", function (fields) {
        if (!fields) return true;
        const keys = fields.map((f) => f.key).filter(Boolean);
        const uniqueKeys = new Set(keys);
        if (keys.length !== uniqueKeys.size) {
          const duplicates = keys.filter(
            (key, index) => keys.indexOf(key) !== index,
          );
          if (duplicates.length > 0) {
            const firstDuplicateIndex = fields.findIndex(
              (f, index) =>
                f.key === duplicates[0] &&
                fields.findIndex((field) => field.key === duplicates[0]) !==
                  index,
            );
            return this.createError({
              path: `${this.path}[${firstDuplicateIndex}].key`,
              message: `Field key "${duplicates[0]}" is already used`,
            });
          }
        }
        return true;
      }),
  })
  .nullable()
  .default(null);

// =============================================================================
// TASK SCHEMA
// =============================================================================

const caseTaskSchema = yup.object({
  key: yup
    .string()
    .required("Task key cannot be empty")
    .max(
      TASK_KEY_MAX_LENGTH,
      `Task key must be less than ${TASK_KEY_MAX_LENGTH} characters`,
    )
    .test(
      "taskKeyFormat",
      "Task key must contain only lowercase letters, numbers, and underscores.",
      (value) => !value || isValidKey(value),
    ),
  display_name: yup
    .string()
    .required("Task name cannot be empty")
    .max(
      TASK_NAME_MAX_LENGTH,
      `Task name must be less than ${TASK_NAME_MAX_LENGTH} characters`,
    ),
  description: yup
    .string()
    .max(
      MAX_DESCRIPTION_LENGTH,
      `Task description must not be more than ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters`,
    )
    .nullable(),
  form: formSchema,
  _internalId: yup.string().optional(),
  _isNewlyAppended: yup.boolean().optional(),
});

const caseTasksArraySchema = yup
  .array()
  .of(caseTaskSchema)
  .test("uniqueTaskKeys", "Task keys must be unique", function (tasks) {
    if (!tasks) return true;
    const keys = tasks.map((t) => t.key).filter(Boolean);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      const duplicates = keys.filter(
        (key, index) => keys.indexOf(key) !== index,
      );
      if (duplicates.length > 0) {
        const firstDuplicateIndex = tasks.findIndex(
          (t, index) =>
            t.key === duplicates[0] &&
            tasks.findIndex((task) => task.key === duplicates[0]) !== index,
        );
        return this.createError({
          path: `${this.path}[${firstDuplicateIndex}].key`,
          message: `Task key "${duplicates[0]}" is already used by another task`,
        });
      }
    }
    return true;
  });

// =============================================================================
// ACTION SCHEMA
// =============================================================================

const actionFlowSchema = yup
  .object({
    flow_id: yup.string().nullable(),
    flow_slug: yup.string().nullable(),
    flow_version_id: yup.string().nullable(),
    flow_version_name: yup.string().nullable(),
  })
  .nullable()
  .default(null)
  .test(
    "validateDecisionFlow",
    "A decision flow and version must be selected",
    function (value) {
      // If flow is null/undefined, it's valid (flow is optional)
      if (!value) return true;
      // If flow object exists, it must have both flow_id and flow_version_id
      if (!value.flow_id || !value.flow_version_id) {
        return this.createError({
          message: "A decision flow and version must be selected",
        });
      }
      return true;
    },
  );

const caseActionSchema = yup.object({
  key: yup
    .string()
    .required("Action key is required")
    .max(
      CASE_ACTION_KEY_MAX_LENGTH,
      `Action key must be less than ${CASE_ACTION_KEY_MAX_LENGTH} characters`,
    )
    .test(
      "actionKeyFormat",
      "Action key must contain only lowercase letters, numbers, and underscores.",
      (value) => !value || isValidKey(value),
    ),
  display_name: yup
    .string()
    .required("Action name is required")
    .max(
      CASE_ACTION_NAME_MAX_LENGTH,
      `Action name must be less than ${CASE_ACTION_NAME_MAX_LENGTH} characters`,
    ),
  description: yup
    .string()
    .max(
      MAX_DESCRIPTION_LENGTH,
      `Action description must not be more than ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters`,
    )
    .nullable(),
  form: formSchema,
  flow: actionFlowSchema,
  _internalId: yup.string().optional(),
  _isNewlyAppended: yup.boolean().optional(),
});

const caseActionsArraySchema = yup
  .array()
  .of(caseActionSchema)
  .test("uniqueActionKeys", "Action keys must be unique", function (actions) {
    if (!actions) return true;
    const keys = actions.map((a) => a.key).filter(Boolean);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      const duplicates = keys.filter(
        (key, index) => keys.indexOf(key) !== index,
      );
      if (duplicates.length > 0) {
        const firstDuplicateIndex = actions.findIndex(
          (a, index) =>
            a.key === duplicates[0] &&
            actions.findIndex((action) => action.key === duplicates[0]) !==
              index,
        );
        return this.createError({
          path: `${this.path}[${firstDuplicateIndex}].key`,
          message: `Action key "${duplicates[0]}" is already used by another action`,
        });
      }
    }
    return true;
  });

// =============================================================================
// REVIEW ROLE SCHEMA
// =============================================================================

const reviewRoleSchema = yup.object({
  display_name: yup.string().optional(),
  description: yup.string().nullable(),
});

const reviewRolesSchema = yup.lazy((value) => {
  if (!value || typeof value !== "object") {
    return yup.object();
  }
  const shape: Record<string, yup.AnySchema> = {};
  for (const key of Object.keys(value)) {
    shape[key] = reviewRoleSchema;
  }
  return yup.object(shape);
});

// =============================================================================
// CASE BLOCK CONFIG SCHEMA
// =============================================================================

const BLOCK_TITLE_MAX_LENGTH = 128;

const caseBlockConfigSchema = yup.object({
  key: yup
    .string()
    .required("Block key is required")
    .test(
      "blockKeyFormat",
      "Block key must start with a letter and contain only lowercase letters, numbers, and underscores.",
      (value) => !value || isValidKey(value),
    ),
  title: yup
    .string()
    .required("Title is required")
    .max(
      BLOCK_TITLE_MAX_LENGTH,
      `Title must be ${BLOCK_TITLE_MAX_LENGTH} characters or less`,
    ),
  icon: yup.string().nullable(),
  case_block_positioning: yup.object({
    x: yup.number(),
    y: yup.number(),
    w: yup.number(),
    h: yup.number(),
  }),
  config: yup
    .mixed()
    .test(
      "blockInputRequired",
      "Block input is required",
      function (config: unknown) {
        if (!config || typeof config !== "object") return true;
        const configObj = config as Record<string, unknown>;
        const kind = configObj.kind as string | undefined;
        if (!kind) return true;

        // Map block kinds to their input field names
        const blockFieldMapping: Record<string, string> = {
          case_details: "case_id",
          markdown: "source",
          entity_details: "entity",
          event_details: "event",
          related_cases: "entity",
          related_entities: "entity",
          related_events: "entity",
        };

        const fieldName = blockFieldMapping[kind];
        if (!fieldName) return true; // Block kind doesn't require input

        const fieldValue = configObj[fieldName];
        // Check if the field has a value
        if (!fieldValue) {
          return this.createError({
            path: `${this.path}.${fieldName}`,
            message: "Block input is required",
          });
        }
        return true;
      },
    ),
  _internalId: yup.string().optional(),
});

const caseBlockConfigsArraySchema = yup
  .array()
  .of(caseBlockConfigSchema)
  .test("uniqueBlockKeys", "Block keys must be unique", function (blocks) {
    if (!blocks) return true;
    const keys = blocks.map((b) => b.key).filter(Boolean);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      const duplicates = keys.filter(
        (key, index) => keys.indexOf(key) !== index,
      );
      if (duplicates.length > 0) {
        const firstDuplicateIndex = blocks.findIndex(
          (b, index) =>
            b.key === duplicates[0] &&
            blocks.findIndex((block) => block.key === duplicates[0]) !== index,
        );
        return this.createError({
          path: `${this.path}[${firstDuplicateIndex}].key`,
          message: `Block key "${duplicates[0]}" is already used by another block`,
        });
      }
    }
    return true;
  });

// =============================================================================
// WORKFLOW RULES SCHEMA
// =============================================================================

const workflowRuleTaskSchema = yup.object({
  task_key: yup.string().required(),
  _internalId: yup.string().optional(),
});

const workflowRuleActionTaskSchema = yup.object({
  task_key: yup.string().required(),
  _internalId: yup.string().optional(),
});

const workflowRuleAutomationSchema = yup.object({
  field: yup.string().required(),
  value: yup.mixed().required("Please select a value"),
});

const workflowRuleActionSchema = yup.object({
  action_key: yup.string().required("Please select an action"),
  required_tasks_keys: yup.array().of(workflowRuleActionTaskSchema).nullable(),
  automation: yup.array().of(workflowRuleAutomationSchema).nullable(),
  _internalId: yup.string().optional(),
});

const workflowRuleSchema = yup.object({
  workflow_tasks_keys: yup.array().of(workflowRuleTaskSchema).nullable(),
  actions: yup.array().of(workflowRuleActionSchema),
});

const workflowRulesSchema = yup.lazy((value) => {
  if (!value || typeof value !== "object") {
    return yup.mixed().nullable();
  }
  const shape: Record<string, yup.AnySchema> = {};
  for (const key of Object.keys(value)) {
    shape[key] = workflowRuleSchema;
  }
  return yup.object(shape);
});

// =============================================================================
// CASE PREPARATION FLOW SCHEMA
// =============================================================================

const casePreparationFlowSchema = yup
  .object({
    version_name: yup.string().required(),
    flow_slug: yup.string().required(),
  })
  .optional()
  .nullable();

// =============================================================================
// COPILOT SETTINGS SCHEMA
// =============================================================================

const COPILOT_PROMPT_MAX_LENGTH = 70_000;
export const MAX_COPILOT_CONTEXT_FILES = 5;

const copilotSettingsSchema = yup.object({
  copilot_enabled: yup.boolean().required(),
  copilot_prompt: yup
    .string()
    .max(
      COPILOT_PROMPT_MAX_LENGTH,
      `Prompt must be ${COPILOT_PROMPT_MAX_LENGTH.toLocaleString()} characters or less`,
    )
    .defined(),
  websearch_tool_enabled: yup.boolean().required(),
  operating_mode: yup.string().oneOf(["eager", "lazy"]).required(),
  is_run_in_sandbox_enabled: yup.boolean().optional(),
  context_file_ids: yup
    .array()
    .of(yup.string().required())
    .max(
      MAX_COPILOT_CONTEXT_FILES,
      `You can attach up to ${MAX_COPILOT_CONTEXT_FILES} files`,
    )
    .optional(),
});

// =============================================================================
// ATTRIBUTES SCHEMA
// =============================================================================

const attributesSchema = yup.object({
  version_name: yup
    .string()
    .required("Version name is required")
    .max(256, `Version name must be less than 256 characters`),
  case_properties: casePropertiesArraySchema.required(),
  review_roles: reviewRolesSchema,
  file_categories: fileCategoriesArraySchema.required(),
  case_actions: caseActionsArraySchema.required(),
  workflow_rules: workflowRulesSchema,
  case_blocks_configs: caseBlockConfigsArraySchema.required(),
  case_tasks: caseTasksArraySchema.required(),
  case_creation_flow_details: casePreparationFlowSchema,
  copilot_settings: copilotSettingsSchema.optional().nullable(),
});

// =============================================================================
// CROSS-COMPONENT VALIDATION
// =============================================================================

/**
 * Validates that file category and block keys don't conflict with property keys or each other.
 * Properties have priority - errors are shown on file categories or blocks, not properties.
 * Collects all errors to show them together for better user experience.
 */
const validateCrossComponentKeys = (
  values: CaseTemplateFormValues,
): yup.ValidationError | true => {
  const { case_properties, case_blocks_configs, file_categories } =
    values.attributes;

  // Build sets of keys for quick lookup
  const blockConfigKeys = new Set(
    (case_blocks_configs || []).map((block) => block.key),
  );
  const fileCategoryKeys = new Set(
    (file_categories || []).map((cat) => cat.key),
  );
  const propertyKeys = new Set((case_properties || []).map((prop) => prop.key));

  const errors: yup.ValidationError[] = [];

  // Check file category keys against properties and block configs
  for (let i = 0; i < (file_categories || []).length; i++) {
    const cat = file_categories[i];
    if (cat._isDefault) continue;
    if (cat.key && propertyKeys.has(cat.key)) {
      const conflictingProp = case_properties?.find((p) => p.key === cat.key);
      errors.push(
        new yup.ValidationError(
          `Category key "${cat.key}" is already used by a case property "${conflictingProp?.display_name}"`,
          cat.key,
          `attributes.file_categories[${i}].key`,
        ),
      );
    } else if (cat.key && blockConfigKeys.has(cat.key)) {
      const conflictingBlock = case_blocks_configs?.find(
        (b) => b.key === cat.key,
      );
      errors.push(
        new yup.ValidationError(
          `Category key "${cat.key}" is already used by a block config "${conflictingBlock?.title ?? conflictingBlock?.key}"`,
          cat.key,
          `attributes.file_categories[${i}].key`,
        ),
      );
    }
  }

  // Check block config keys against properties and file categories
  for (let i = 0; i < (case_blocks_configs || []).length; i++) {
    const block = case_blocks_configs[i];
    if (block.key && propertyKeys.has(block.key)) {
      const conflictingProp = case_properties?.find((p) => p.key === block.key);
      errors.push(
        new yup.ValidationError(
          `Block key "${block.key}" is already used by a case property "${conflictingProp?.display_name}"`,
          block.key,
          `attributes.case_blocks_configs[${i}].key`,
        ),
      );
    } else if (block.key && fileCategoryKeys.has(block.key)) {
      const conflictingCategory = file_categories?.find(
        (c) => c.key === block.key,
      );
      errors.push(
        new yup.ValidationError(
          `Block key "${block.key}" is already used by a file category "${conflictingCategory?.display_name}"`,
          block.key,
          `attributes.case_blocks_configs[${i}].key`,
        ),
      );
    }
  }

  if (errors.length > 0) {
    return new yup.ValidationError(errors);
  }

  return true;
};

// =============================================================================
// MAIN SCHEMA
// =============================================================================

/**
 * Main validation schema for CaseTemplateFormValues.
 * This schema validates the form data ensuring all fields meet requirements,
 * regardless of which step is currently mounted.
 */
export const caseTemplateFormSchema = yup
  .object({
    id: yup.string().required(),
    type: yup.string().required(),
    attributes: attributesSchema,
  })
  .test({
    name: "crossComponentUniqueKeys",
    message:
      "Keys must be unique across properties, file categories, and block configs",
    test: (values) => {
      if (!values) return true;
      const result = validateCrossComponentKeys(
        values as unknown as CaseTemplateFormValues,
      );
      if (result !== true) {
        // Throw directly to preserve multiple inner errors
        throw result;
      }
      return true;
    },
  });
